import type { DynamicConfigurationForRepeatableSlots } from "../../plugins/RepeatableSlotsPlugin/RepeatableSlots.types";
import type { DynamicConfigurationForDynamicSlot } from "../../plugins/DynamicSlotPlugin/DynamicSlot.types";
import type { Comparator, SizeTuple } from "../../helpers/slotSizes";

/*
 *
 * Bidders Configurations
 *
 */

// This must be declared globally to ensure that all bidder plugins can extend it and
// the declaration merging will work correctly. Othwerise the extensions may not be
// recognized in the config files to properly type check.
declare global {
  /**
   * Configuration of all bidders for a slot. This is indexed by the bidder plugin
   * name, currently `prebid` and/or `a9`. The value for each is the specific
   * configuration structure for that bidder.
   *
   * The configuration structure and bidder names are set via the Bidder plugins typing.
   * They should globally extend the `SlotBiddersConfig` interface to define their own
   * configuration structure for the slot. All the interface will be merged using declaration
   * merging to strongly type the `SlotBiddersConfig`
   *
   * For example, given the prebid plugin, it will define its own configuration structure in it's
   * own prebid.d.ts file:
   * ```ts
   * interface SlotBiddersConfig {
   *   prebid?: PrebidSlotBiddersConfig;
   * }
   * ```
   *
   * If the types file does not globally define the interface, then wrap the type in a global
   * declaration block to ensure the platform types and confguration files will pick up the
   * merged interface:
   *
   * ```ts
   * declare global {
   *  interface SlotBiddersConfig {
   *   prebid?: PrebidSlotBiddersConfig;
   *  }
   * }
   * ```
   */
  // Disable  no-empty-object-type rule, as we want to allow empty objects here specifically
  // to allow for the bidders to define their own configuration structure within their plugin.
  // This will isolate the slot configuration code to the specific plugin and leave this
  // typing to handle just the generic structure of the bidders configuration.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface SlotBiddersConfig {}

  /**
   * The source of the bidder configuration, which is references a specific
   * bidder plugin implementation. This will be a list of strings for each
   * supported Bidder that has added a configuration to the SlotBiddersConfig
   * object. For example, currently this will be `"prebid" | "a9"`
   *
   * This source is used to define the bidder configuration for a specific slot
   * as well as to get/set the bidder configuration for that slot on the AdNode
   * at runtime by the plugins.
   */
  export type BidderSource = keyof SlotBiddersConfig;

  /**
   * Mapping of all bidder sources to their specific ad unit configuration types.
   * This is used by the `onCreateBidderAdUnit` hook to ensure that the adUnit is
   * strongly typed when using the `isBidderAdUnit` type guard.
   *
   * ```ts
   * hooks: {
   *  onCreateBidderAdUnit: (bidderSource, auctionData, adUnit) => {
   *   if (isBidderAdUnit("prebid", bidderSource, adUnit)) {
   *    // adUnit is PrebidAdUnit type here
   *   } else if (isBidderAdUnit("a9", bidderSource, adUnit)) {
   *   // adUnit is A9AdUnit type here
   *  }
   * }
   * ```
   */
  // Disable  no-empty-object-type rule, as we want to allow empty objects here specifically
  // to allow for the bidders to define their own configuration structure within their plugin.
  // This will isolate the slot configuration code to the specific plugin and leave this
  // typing to handle just the generic structure of the bidders configuration.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface BidderAdUnitConfigMap {}
}

/**
 * Determines the correct typing for an ad unit based on the intended bidder source.
 * Distinguishes between A9AdUnit and PrebidAdUnit[]
 * @param expectedSource {S extends BidderSource} expected bidder source
 * @param bidderSource {BidderSource} actual bidder source
 * @param adUnit {unknown} ad unit configuration object
 * @return {boolean>} true if the ad unit is of the expected type
 */
export function isBidderAdUnit<S extends BidderSource>(
  expectedSource: S,
  bidderSource: BidderSource,
  adUnit: unknown,
): adUnit is NonNullable<BidderAdUnitConfigMap[S]> {
  return (
    expectedSource === bidderSource && adUnit !== undefined && adUnit !== null
  );
}

/*
 *
 * Slot Names
 *
 */

export type TargetingKey = "src" | "pos" | "loc";
export type BaseSlotName =
  | "top_leaderboard"
  | "top_boxad"
  | "incontent_leaderboard"
  | "incontent_boxad"
  | "interstitial"
  | "bottom_leaderboard"
  | "floor_adhesion"
  | "mid_boxad"
  | "lightbox_leaderboard";
export type VideoSlotName = "featured" | "incontent_player";

/**
 * Base slot names with a repeatable suffix, such as `top_leaderboard_1`,
 * `top_leaderboard_2`, etc.
 */
export type SlotNameWithSuffix = `${BaseSlotName}_${number}`;

/**
 * Combination type of all possible slot names including configuration base slot
 * names, all video slot names, and the repeatable slots.
 */
export type SlotName = BaseSlotName | SlotNameWithSuffix | VideoSlotName;

/*
 *
 * Slot Configuration
 *
 */

export type SlotType = "display" | "video";

/**
 * The placement group for a slot is part of the Ad Unit Path grouping some
 * slots together, like leaderboards into `LB`. This group is added to the
 * second section of the Ad Unit Path along with the server prefix, such as
 * `/5441/wka1b.MR/top_boxad...`
 *
 * - LB: Leaderboard
 * - MR: Medium Rectangle
 * - ILB: Inline Leaderboard
 * - HiVi: High Visibility
 * - IG: In-Gallery
 * - PF: Page Floor
 */
export type PlacementGroup =
  | "LB"
  | "MR"
  | "ILB"
  | "HiVi"
  | "IU"
  | "PF"
  | "IG"
  | "VIDEO";

/**
 * Contains properties related to slot size removal after impression.
 */
export type RefreshConfigRemoveSizes = {
  /**
   * Number of times the slot will have its size removed after impression.
   * - If set to `once`, the size will be removed only once after the first impression.
   * - If set to `always`, the size will be removed after every impression.
   * */
  strategy: "once" | "always";
  /**
   * Size that will be used to limit the slot size.
   * - if "height", the slot will be limited by height ("width" will be set to 0 on comparator)
   * - if "width", the slot will be limited by width ("height" will be set to 0 on comparator)
   * - if "both", the slot will be limited by both width and height at the same time
   */
  match: "height" | "width" | "both";
  /**
   * Type of comparator that will be used to compare the slot size and limit the size.
   */
  with: Comparator;
};

/**
 * Contains properties related to slot refreshing
 * */
type RefreshConfig = {
  /**
   * Determines whether the slot is refreshable
   * */
  enabled: boolean;

  /**
   * Configuration for removing sizes after impression.
   * If not provided, the slot will not remove sizes after impression.
   * Otherwise it will remove sizes based on the specified configuration.
   *
   * Alternatively it can be set to `false` to disable size removal.
   * If set to `true`, it will use the default configuration
   * with `strategy: "once"`, `match: "height"` and `with: Comparator.GREATER_THAN`.
   */
  removeSizes?: boolean | RefreshConfigRemoveSizes;
  exactSizesToRemove?: SizeTuple[];
};

export type SlotConfig = {
  name: SlotName;
  // temporary flag to indicate that the slot is a `<fandom-ad />` web component
  // will not be initialized by AdNodeManager
  webComponent?: boolean;
  /**
   * The slot element type. Somewhat in sync with `PrebidMediaType` (sans
   * "native"), but defaults to "display" for backwards compatibility and the
   * fact of it being more common.
   */
  type?: SlotType;
  /**
   * Default sizes for the slot that will be used to configure the slot when
   * registering with GPT (googletag.defineSlot).
   */
  sizes: googletag.GeneralSize;
  /**
   * Responsive sizes for the slot that will be used added to the slot GPT (googletag)
   * configuration using a ResponsiveSizeMapping. When present GPT will use this
   * over the `sizes` property when rendering the slot.
   */
  responsiveSizes: googletag.SizeMappingArray;
  /**
   * The placement group this slot is in for generating the ad unit path. For
   * example top_leaderboard is in the group `lb` and floor_adhesion is in `pf`
   */
  group: PlacementGroup;
  /**
   * Default targeting that will be applied to the slot when it is defined in
   * GPT.
   */
  targeting: {
    [key in TargetingKey]?: string;
  };
  /**
   * Setting that indicates whether this slot should be called in the first ad
   * request, even if it's not in the viewport.
   */
  isFirstCall?: boolean;
  companion?: boolean;
  refresh?: RefreshConfig;
  /**
   * Dynamic slot configuration
   * If present, the slot element will be created and
   *
   * inserted into the DOM according to the configuration
   */
  dynamic?: DynamicConfigurationForRepeatableSlots &
    DynamicConfigurationForDynamicSlot;
  /**
   * Configuration of all bidders for this slot.
   *
   * If undefined then the slot will not bid for programmatic client side
   * (though GAM may still bid for it if configured to do so).
   */
  bidders?: SlotBiddersConfig;
};

export type SlotConfigMap = {
  baseSlots: {
    [key in BaseSlotName]?: SlotConfig;
  };
  videoSlots: {
    [key in VideoSlotName]?: SlotConfig;
  };
};
